import {
  OptimizelyDecisionWithOutUserContext,
  getExperimentVariables,
  getExperimentVariationKey,
  isExperimentEnabled,
} from '@awaze/mfe-common.lib.optimizely'
import React, { ReactNode, useContext, createContext } from 'react'

export interface OptimizelyState {
  decisions?: Record<string, OptimizelyDecisionWithOutUserContext>
  dataFile?: string
  userId?: string
  brand?: string
  locale?: string
  userAgent?: string
  userDeviceType?: string
  host?: string
  isGuestApp?: boolean
}

export interface OptimizelyDecisionsProps extends OptimizelyState {
  isExperimentEnabled: (experimentKey: string) => boolean
  isExperimentVariationKeyOneOf: (
    experimentKey: string,
    variationKeys: Array<string>,
  ) => boolean
  getExperimentVariationKey: (experimentKey: string) => string | undefined
  getExperimentVariables: (
    experimentKey: string,
  ) => OptimizelyDecisionWithOutUserContext['variables'] | undefined
}

const OptimizelyDecisionContext =
  createContext<OptimizelyDecisionsProps | null>(null)

export const useOptimizelyDecisionsContext = (): OptimizelyDecisionsProps => {
  const context = useContext(OptimizelyDecisionContext)

  if (!context) {
    throw new Error(
      'useOptimizelyDecisionsContext must be used within OptimizelyDecisionContextProvider',
    )
  }

  return context
}

export const OptimizelyDecisionContextProvider: React.FC<{
  children: ReactNode
  optimizelyData: OptimizelyState
}> = ({ children, optimizelyData }) => {
  const {
    decisions = {},
    dataFile = '',
    userId,
    userAgent,
    userDeviceType = '',
    locale,
    brand,
    host,
    isGuestApp,
  }: OptimizelyState = optimizelyData

  return (
    <OptimizelyDecisionContext.Provider
      value={{
        decisions,
        dataFile,
        userId,
        brand,
        locale,
        host,
        userAgent,
        userDeviceType,
        isGuestApp,
        isExperimentEnabled: (experimentKey: string) => {
          return isExperimentEnabled(decisions, experimentKey)
        },
        isExperimentVariationKeyOneOf: (
          experimentKey: string,
          variationKeys: Array<string>,
        ) => {
          const variationKey = getExperimentVariationKey(
            decisions,
            experimentKey,
          )

          if (!variationKey) {
            return false
          }

          return variationKeys.includes(variationKey)
        },
        getExperimentVariationKey: (experimentKey: string) => {
          return getExperimentVariationKey(decisions, experimentKey)
        },
        getExperimentVariables: (experimentKey: string) => {
          return getExperimentVariables(decisions, experimentKey)
        },
      }}
    >
      {children}
    </OptimizelyDecisionContext.Provider>
  )
}
